import {
  SIDENAV_COLLAPSED_WIDTH,
  SMALL_SCREEN_WIDTH_THRESHOLD,
} from '@abb-procure/constants';
import { PreferencesState } from '@abb-procure/data';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  TemplateRef,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { FileHashPipe } from '../../pipes/file-hash.pipe';
import {
  SidePanelTabService,
  SidenavService,
  type SidePanelTab,
} from '../../services/sidenav.service';
import { pxToRem } from '../../utilities/px-to-rem.util';
import { MenuItem } from '../menu-item/menu-item.component';
import { Menu } from '../menu/menu.component';
import { ResizeHandler } from '../resize-handler/resize-handler.component';
import { SidenavToggleButton } from '../sidenav-toggle-button/sidenav-toggle-button.component';

enum Views {
  Insights = 0,
  Copilot = 1,
  Documents = 2,
  ParsedData = 3,
}

@Component({
  selector: 'procure-side-panel',
  templateUrl: './side-panel.component.html',
  styleUrl: './side-panel.component.scss',
  imports: [
    Menu,
    MenuItem,
    NgTemplateOutlet,
    MatButtonToggle,
    MatButtonToggleGroup,
    ReactiveFormsModule,
    FileHashPipe,
    ResizeHandler,
    SidenavToggleButton,
  ],
  providers: [SidenavService],
  host: {
    '[hidden]': '!insights() && !copilot() && !documents() && !parsedData()',
    '[class.expanded]': 'isExpanded()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidePanel {
  readonly hostElement = inject(ElementRef);

  readonly insights = contentChild<TemplateRef<unknown>>('insights');
  readonly copilot = contentChild<TemplateRef<unknown>>('copilot');
  readonly documents = contentChild<TemplateRef<unknown>>('documents');
  readonly parsedData = contentChild<TemplateRef<unknown>>('parsedData');
  readonly preferences = inject(PreferencesState);

  readonly preferredWidth = input<number | undefined>();
  readonly preferredView = input<SidePanelTab | undefined>();
  readonly setPreferredWidth = output<number>();

  protected readonly viewControl = new FormControl<number>(Views.Insights);
  protected readonly activeView = toSignal(this.viewControl.valueChanges, {
    initialValue: this.viewControl.value,
  });
  protected readonly Views = Views;
  protected readonly sidenavService = inject(SidenavService);
  protected isExpanded = this.sidenavService.isExpanded;
  protected readonly hasMultipleViews = computed(
    () =>
      [
        this.insights(),
        this.copilot(),
        this.documents(),
        this.parsedData(),
      ].filter((view) => !!view).length >= 2,
  );

  protected resizingEvent = {
    isResizing: false,
    startingCursorX: 0,
    startingWidth: 0,
  };

  private readonly sidePanelTabService = inject(SidePanelTabService);
  private readonly destroyRef = inject(DestroyRef);
  private hasUserSelectedView = false;

  private readonly tabNameToView: Record<SidePanelTab, Views> = {
    insights: Views.Insights,
    copilot: Views.Copilot,
    documents: Views.Documents,
    parsedData: Views.ParsedData,
  };

  constructor() {
    effect(() => {
      const preferredWidth = this.preferredWidth();
      if (preferredWidth) {
        this.sidenavService.setSidenavWidth(preferredWidth);
      } else {
        this.sidenavService.setBaseWidth();
      }
    });

    effect(() => this.syncViewWithAvailableViews());

    effect(() => {
      if (
        this.preferences.isNavigationExpanded() &&
        window.innerWidth <= SMALL_SCREEN_WIDTH_THRESHOLD
      ) {
        this.setPreferredWidth.emit(SIDENAV_COLLAPSED_WIDTH);
      }
    });

    this.viewControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.hasUserSelectedView = true;
      });

    // Listen for external requests to open specific tabs (e.g., from "Chat with file" button)
    this.sidePanelTabService.openTabRequest$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tab) => this.openTab(tab));
  }

  private syncViewWithAvailableViews(): void {
    const availableViews = [
      { type: Views.Insights, exists: this.insights() },
      { type: Views.Copilot, exists: this.copilot() },
      { type: Views.Documents, exists: this.documents() },
      { type: Views.ParsedData, exists: this.parsedData() },
    ];
    const preferredView = this.preferredView();
    untracked(() => {
      const currentViewExists = availableViews.some(
        (v) => v.type === this.viewControl.value && v.exists,
      );
      if (currentViewExists && this.hasUserSelectedView) {
        return;
      }

      const initialView =
        availableViews.find(
          (v) =>
            !!preferredView &&
            v.type === this.tabNameToView[preferredView] &&
            v.exists,
        ) ?? availableViews.find((v) => v.exists);
      if (initialView && initialView.type !== this.viewControl.value) {
        this.viewControl.setValue(initialView.type);
        this.hasUserSelectedView = false;
      }
    });
  }

  /**
   * Opens a specific tab and expands the panel if collapsed.
   */
  private openTab(tab: SidePanelTab): void {
    const view = this.tabNameToView[tab];

    // Check if the tab exists
    const tabExists =
      (tab === 'copilot' && this.copilot()) ||
      (tab === 'insights' && this.insights()) ||
      (tab === 'documents' && this.documents()) ||
      (tab === 'parsedData' && this.parsedData());

    if (!tabExists) {
      return;
    }

    // Set the view
    this.viewControl.setValue(view);

    // Expand the panel if it's collapsed
    if (!this.sidenavService.isExpanded()) {
      this.collapseNavPanelOnSmallScreens();
      this.sidenavService.setBaseWidth();
      this.setPreferredWidth.emit(this.sidenavService.width());
    }
  }

  @HostListener('window:mousemove', ['$event'])
  updateSidenavWidth(event: MouseEvent): void {
    if (!this.resizingEvent.isResizing) {
      return;
    }
    this.sidenavService.resizeSidenav(
      this.resizingEvent.startingCursorX,
      event.clientX,
      this.resizingEvent.startingWidth,
    );
  }

  @HostListener('window:mouseup')
  stopResizing(): void {
    if (this.resizingEvent.isResizing) {
      this.setPreferredWidth.emit(this.sidenavService.width());
    }
    this.resizingEvent.isResizing = false;
  }

  protected collapseNavPanelOnSmallScreens(): void {
    if (
      !this.sidenavService.isExpanded() &&
      this.preferences.isNavigationExpanded() &&
      window.innerWidth <= SMALL_SCREEN_WIDTH_THRESHOLD
    ) {
      this.preferences.toggleNavigation();
    }
  }

  protected toggleExpansion(): void {
    this.collapseNavPanelOnSmallScreens();

    this.sidenavService.toggleExpansion();
    this.setPreferredWidth.emit(this.sidenavService.width());
  }

  protected startResizing(event: MouseEvent): void {
    const currentHostElementWidth = this.hostElement.nativeElement.offsetWidth;
    this.resizingEvent = {
      isResizing: true,
      startingCursorX: pxToRem(event.clientX),
      startingWidth: pxToRem(currentHostElementWidth),
    };
  }

  protected expandPanel(item: Views): void {
    this.viewControl.setValue(item);
    this.toggleExpansion();
  }
}
